import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  collection, 
  query, 
  getDocs, 
  orderBy, 
  limit, 
  startAfter,
  getDoc, 
  doc,
  where
} from 'firebase/firestore';
import { ref } from 'firebase/storage';
import { firestore, storage } from '../../firebase/config';
import BusinessCard from '../cards/BusinessCard';

const BusinessListPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Parse query parameters from the URL
  const searchParams = new URLSearchParams(location.search);
  const initialSearchQuery = searchParams.get('query') || '';
  const initialLocationQuery = searchParams.get('location') || '';
  
  // State variables
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [locationQuery, setLocationQuery] = useState(initialLocationQuery);
  const [showFilters, setShowFilters] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    businessType: ''
  });
  
  // Fetch businesses on component mount and when search parameters change
  useEffect(() => {
    fetchBusinesses();
  }, [initialSearchQuery, initialLocationQuery]);
  
  // Handle search form submission
  const handleSearch = (e) => {
    e.preventDefault();
    
    // Update URL with search parameters
    const params = new URLSearchParams();
    if (searchQuery) params.set('query', searchQuery);
    if (locationQuery) params.set('location', locationQuery);
    
    navigate({
      pathname: '/dashboard/businesses',
      search: params.toString()
    });
    
    // Reset pagination and fetch new results
    setLastDoc(null);
    setHasMore(true);
    fetchBusinesses(true);
  };
  
  // Handle filter changes
  const handleFilterChange = (e) => {
    setFilterOptions({
      ...filterOptions,
      [e.target.name]: e.target.value
    });
  };
  
  // Fetch businesses from Firestore
  const fetchBusinesses = async (isNewSearch = false) => {
    try {
      setLoading(isNewSearch || businesses.length === 0);
      console.log('Fetching businesses...');
      
      // Collection reference
      const businessesRef = collection(firestore, 'businesses');
      
      // Start with a basic query to get all businesses, then filter client-side
      // This is more reliable when we're not sure about the data structure
      let businessesQuery = query(
        businessesRef,
        orderBy('createdAt', 'desc'),
        limit(30) // Increase limit to get more results
      );
      
      console.log('Executing basic business query to fetch initial data');
      const businessesSnapshot = await getDocs(businessesQuery);
      
      console.log(`Retrieved ${businessesSnapshot.size} total businesses from database`);
      
      // Log structure of the first document to debug
      if (businessesSnapshot.size > 0) {
        const sampleData = businessesSnapshot.docs[0].data();
        console.log('Sample business document structure:', sampleData);
        console.log('Sample business fields:', Object.keys(sampleData));
        
        // Save last document for pagination
        setLastDoc(businessesSnapshot.docs[businessesSnapshot.docs.length - 1]);
      }
      
      if (businessesSnapshot.empty) {
        console.log('No businesses found in the database at all - database may be empty');
        setHasMore(false);
        setLoading(false);
        setLoadingMore(false);
        setBusinesses([]);
        return;
      }
      
      // Process businesses data
      const allBusinesses = businessesSnapshot.docs.map(doc => {
        const data = doc.data();
        const businessId = doc.id;
        
        // Get city from address or location (with safe access)
        const businessLocation = 
          (data.address && data.address.city) ? data.address.city : 
          (data.location && data.location.city) ? data.location.city : 
          'Unknown Location';
        
        // Get description (prioritize about over description)
        const description = data.about || data.description || 'No description available';
        
        // Get image and logo paths (with safe access)
        const imagePath = (data.businessImage && data.businessImage.path) ? data.businessImage.path : null;
        const logoPath = (data.logo && data.logo.path) ? data.logo.path : null;
        
        return {
          id: businessId,
          name: data.name || 'Unknown Business',
          description: description,
          address: data.address?.street
            ? `${data.address.city || ''}, ${data.address.state || ''} ${data.address.zipcode || ''}`
            : 'Address not specified',
          logo: data.logoUrl || null,
          image: data.imageUrl || null,
          imagePath: imagePath,
          logoPath: logoPath,
          type: data.type || 'Not specified',
          employeeCount: data.employeeScale || 'Not specified',
          yearFounded: data.yearFounded || null,
          city: businessLocation,
          // More comprehensive search terms
          searchTerms: `${data.name || ''} ${description || ''} ${data.type || ''} ${businessLocation || ''}`.toLowerCase()
        };
      });
      
      console.log(`Processed ${allBusinesses.length} businesses`);
      
      // Now filter the businesses based on search criteria on the client side
      let filteredBusinesses = allBusinesses;
      
      if (searchQuery) {
        console.log(`Filtering businesses by search query: "${searchQuery}"`);
        const searchTerms = searchQuery.toLowerCase().split(' ').filter(term => term.length > 0);
        
        filteredBusinesses = allBusinesses.filter(business => {
          // Check multiple fields for matches
          const nameMatch = business.name && searchTerms.some(term => 
            business.name.toLowerCase().includes(term));
          
          const descriptionMatch = business.description && searchTerms.some(term => 
            business.description.toLowerCase().includes(term));
            
          const typeMatch = business.type && searchTerms.some(term => 
            business.type.toLowerCase().includes(term));
          
          return nameMatch || descriptionMatch || typeMatch;
        });
        
        console.log(`Found ${filteredBusinesses.length} businesses matching search query "${searchQuery}"`);
      }
      
      if (locationQuery) {
        console.log(`Filtering businesses by location: "${locationQuery}"`);
        const locationTerms = locationQuery.toLowerCase().split(' ').filter(term => term.length > 0);
        
        filteredBusinesses = filteredBusinesses.filter(business => {
          // Check location fields for matches
          const cityMatch = business.city && locationTerms.some(term => 
            business.city.toLowerCase().includes(term));
            
          const addressMatch = business.address && locationTerms.some(term => 
            business.address.toLowerCase().includes(term));
          
          return cityMatch || addressMatch;
        });
        
        console.log(`Found ${filteredBusinesses.length} businesses matching location "${locationQuery}"`);
      }
      
      // Apply business type filter if selected
      if (filterOptions.businessType) {
        console.log(`Filtering businesses by type: "${filterOptions.businessType}"`);
        
        filteredBusinesses = filteredBusinesses.filter(business => 
          business.type && business.type.toLowerCase().includes(filterOptions.businessType.toLowerCase())
        );
        
        console.log(`Found ${filteredBusinesses.length} businesses after applying type filter`);
      }
      
      if (filteredBusinesses.length === 0) {
        console.log('No businesses found matching all criteria after filtering');
      } else {
        console.log(`Final filtered results: ${filteredBusinesses.length} businesses`);
      }
      
      // Update businesses state
      if (isNewSearch) {
        setBusinesses(filteredBusinesses);
      } else {
        // For pagination, we should technically implement a more sophisticated solution
        // For now, we'll just add unique businesses we don't already have
        const existingIds = new Set(businesses.map(business => business.id));
        const newUniqueBusinesses = filteredBusinesses.filter(business => !existingIds.has(business.id));
        
        setBusinesses(prevBusinesses => [...prevBusinesses, ...newUniqueBusinesses]);
      }
      
      setHasMore(filteredBusinesses.length >= 12); // If we got fewer than the limit, there's no more
      setLoading(false);
      setLoadingMore(false);
    } catch (error) {
      console.error('Error fetching businesses:', error);
      setLoading(false);
      setLoadingMore(false);
    }
  };
  
  // Apply filters - this is now embedded directly in the fetchBusinesses function
  const applyFilters = (businessesData) => {
    let filtered = [...businessesData];
    
    // First apply search query filters
    if (searchQuery) {
      console.log(`Applying search query filter at render time: "${searchQuery}"`);
      const searchTerms = searchQuery.toLowerCase().split(' ').filter(term => term.length > 0);
      
      filtered = filtered.filter(business => {
        // Check multiple fields for matches
        const nameMatch = business.name && searchTerms.some(term => 
          business.name.toLowerCase().includes(term));
        
        const descriptionMatch = business.description && searchTerms.some(term => 
          business.description.toLowerCase().includes(term));
          
        const typeMatch = business.type && searchTerms.some(term => 
          business.type.toLowerCase().includes(term));
        
        return nameMatch || descriptionMatch || typeMatch;
      });
      
      console.log(`After search query filter: ${filtered.length} businesses`);
    }
    
    // Apply location filter
    if (locationQuery) {
      console.log(`Applying location filter at render time: "${locationQuery}"`);
      const locationTerms = locationQuery.toLowerCase().split(' ').filter(term => term.length > 0);
      
      filtered = filtered.filter(business => {
        // Check location fields for matches
        const cityMatch = business.city && locationTerms.some(term => 
          business.city.toLowerCase().includes(term));
          
        const addressMatch = business.address && locationTerms.some(term => 
          business.address.toLowerCase().includes(term));
        
        return cityMatch || addressMatch;
      });
      
      console.log(`After location filter: ${filtered.length} businesses`);
    }
    
    // Apply business type filter if selected
    if (filterOptions.businessType) {
      console.log(`Applying business type filter at render time: "${filterOptions.businessType}"`);
      
      filtered = filtered.filter(business => 
        business.type && business.type.toLowerCase().includes(filterOptions.businessType.toLowerCase())
      );
      
      console.log(`After business type filter: ${filtered.length} businesses`);
    }
    
    return filtered;
  };
  
  // Load more businesses
  const handleLoadMore = () => {
    if (!hasMore || loadingMore) return;
    
    if (!lastDoc) {
      console.error("Cannot load more: No reference document available");
      setHasMore(false);
      return;
    }
    
    setLoadingMore(true);
    fetchMoreBusinesses();
  };
  
  // Separate function to fetch more businesses for pagination
  const fetchMoreBusinesses = async () => {
    try {
      // We need to use the lastDoc for true pagination
      if (!lastDoc) {
        console.log('No last document reference for pagination');
        setLoadingMore(false);
        setHasMore(false);
        return;
      }
      
      const businessesRef = collection(firestore, 'businesses');
      
      // Create a properly paginated query
      const paginatedQuery = query(
        businessesRef,
        orderBy('createdAt', 'desc'),
        startAfter(lastDoc),  // Use the last document for proper pagination
        limit(15)  // Get next batch
      );
      
      console.log('Executing paginated query to fetch more businesses');
      const businessesSnapshot = await getDocs(paginatedQuery);
      
      if (businessesSnapshot.empty) {
        console.log('No more businesses to load');
        setHasMore(false);
        setLoadingMore(false);
        return;
      }
      
      // Save last document for next pagination
      setLastDoc(businessesSnapshot.docs[businessesSnapshot.docs.length - 1]);
      
      // Process the new businesses
      const newBusinesses = businessesSnapshot.docs.map(doc => {
        const data = doc.data();
        const businessId = doc.id;
        
        // Get city from address or location (with safe access)
        const businessLocation = 
          (data.address && data.address.city) ? data.address.city : 
          (data.location && data.location.city) ? data.location.city : 
          'Unknown Location';
        
        // Get description (prioritize about over description)
        const description = data.about || data.description || 'No description available';
        
        // Get image and logo paths (with safe access)
        const imagePath = (data.businessImage && data.businessImage.path) ? data.businessImage.path : null;
        const logoPath = (data.logo && data.logo.path) ? data.logo.path : null;
        
        return {
          id: businessId,
          name: data.name || 'Unknown Business',
          description: description,
          address: data.address?.street
            ? `${data.address.city || ''}, ${data.address.state || ''} ${data.address.zipcode || ''}`
            : 'Address not specified',
          logo: data.logoUrl || null,
          image: data.imageUrl || null,
          imagePath: imagePath,
          logoPath: logoPath,
          type: data.type || 'Not specified',
          employeeCount: data.employeeScale || 'Not specified',
          yearFounded: data.yearFounded || null,
          city: businessLocation,
          // More comprehensive search terms
          searchTerms: `${data.name || ''} ${description || ''} ${data.type || ''} ${businessLocation || ''}`.toLowerCase()
        };
      });
      
      console.log(`Fetched ${newBusinesses.length} additional businesses`);
      
      // Filter the new businesses based on search criteria
      let filteredNewBusinesses = newBusinesses;
      
      if (searchQuery) {
        const searchTerms = searchQuery.toLowerCase().split(' ').filter(term => term.length > 0);
        
        filteredNewBusinesses = newBusinesses.filter(business => {
          const nameMatch = business.name && searchTerms.some(term => 
            business.name.toLowerCase().includes(term));
          
          const descriptionMatch = business.description && searchTerms.some(term => 
            business.description.toLowerCase().includes(term));
            
          const typeMatch = business.type && searchTerms.some(term => 
            business.type.toLowerCase().includes(term));
          
          return nameMatch || descriptionMatch || typeMatch;
        });
      }
      
      if (locationQuery) {
        const locationTerms = locationQuery.toLowerCase().split(' ').filter(term => term.length > 0);
        
        filteredNewBusinesses = filteredNewBusinesses.filter(business => {
          const cityMatch = business.city && locationTerms.some(term => 
            business.city.toLowerCase().includes(term));
            
          const addressMatch = business.address && locationTerms.some(term => 
            business.address.toLowerCase().includes(term));
          
          return cityMatch || addressMatch;
        });
      }
      
      // Apply business type filter if selected
      if (filterOptions.businessType) {
        filteredNewBusinesses = filteredNewBusinesses.filter(business => 
          business.type && business.type.toLowerCase().includes(filterOptions.businessType.toLowerCase())
        );
      }
      
      // Check for duplicates using a Set of IDs
      const existingIds = new Set(businesses.map(business => business.id));
      const uniqueNewBusinesses = filteredNewBusinesses.filter(business => !existingIds.has(business.id));
      
      console.log(`Adding ${uniqueNewBusinesses.length} unique new businesses after filtering`);
      
      // Update businesses state by appending unique new businesses
      setBusinesses(prevBusinesses => [...prevBusinesses, ...uniqueNewBusinesses]);
      setHasMore(uniqueNewBusinesses.length > 0);
      setLoadingMore(false);
    } catch (error) {
      console.error('Error fetching more businesses:', error);
      setLoadingMore(false);
    }
  };
  
  // Apply client-side filters (use this legacy function for compatibility)
  const filteredBusinesses = applyFilters(businesses);
  
  return (
    <div className="flex-1 bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Browse Businesses
            </h1>
            <p className="text-gray-600">Discover great businesses in your area</p>
          </div>
          <button 
            onClick={() => navigate('/dashboard/jobs')}
            className="px-4 py-2 text-sm bg-white border border-[#FF8C00] text-[#FF8C00] rounded hover:bg-[#FF8C00]/10 transition flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            View Jobs
          </button>
        </div>
        
        {/* Search Bar */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <form onSubmit={handleSearch}>
            <div className="flex flex-col md:flex-row gap-4 mb-4">
              <div className="flex-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Business name or type"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent transition-all"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="flex-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="City or location"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent transition-all"
                  value={locationQuery}
                  onChange={(e) => setLocationQuery(e.target.value)}
                />
              </div>
              <button
                type="submit"
                className="px-6 py-3 bg-[#FF8C00] text-white rounded-lg hover:bg-[#FF8C00]/90 transition-colors flex-shrink-0 font-medium"
              >
                Search Businesses
              </button>
            </div>
            <div className="flex justify-between items-center">
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className="text-sm text-[#FF8C00] hover:text-[#FF8C00]/80 flex items-center transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
                </svg>
                {showFilters ? 'Hide filters' : 'Show filters'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setFilterOptions({ businessType: '' });
                  setSearchQuery('');
                  setLocationQuery('');
                  fetchBusinesses(true);
                }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Reset filters
              </button>
            </div>
            
            {/* Filters Section */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Type</label>
                  <select 
                    name="businessType"
                    value={filterOptions.businessType}
                    onChange={handleFilterChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent"
                  >
                    <option value="">All Types</option>
                    <option value="restaurant">Restaurant & Hospitality</option>
                    <option value="retail">Retail</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="technology">Technology</option>
                    <option value="education">Education</option>
                    <option value="services">Professional Services</option>
                  </select>
                </div>
              </div>
            )}
          </form>
        </div>
        
        {/* Results Section */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-800">
              {searchQuery || locationQuery 
                ? `Search Results${searchQuery ? ` for "${searchQuery}"` : ''}${locationQuery ? ` in "${locationQuery}"` : ''}` 
                : 'All Businesses'}
            </h2>
            <span className="text-sm text-gray-500">{filteredBusinesses.length} businesses found</span>
          </div>
          
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FF8C00]"></div>
            </div>
          ) : filteredBusinesses.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {filteredBusinesses.map(business => (
                  <BusinessCard 
                    key={`business-${business.id}`}
                    logo={business.logo}
                    imagePath={business.imagePath}
                    name={business.name}
                    description={business.description}
                    address={business.address}
                    businessId={business.id}
                  />
                ))}
              </div>
              
              {hasMore && (
                <div className="mt-8 text-center">
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="px-6 py-3 bg-white border border-[#FF8C00] text-[#FF8C00] rounded-lg hover:bg-[#FF8C00]/10 transition-colors font-medium disabled:opacity-50"
                  >
                    {loadingMore ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-[#FF8C00]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading more...
                      </span>
                    ) : 'Load More Businesses'}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <div className="bg-gray-100 p-6 inline-block rounded-full mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">No businesses found</h3>
              <p className="text-gray-600 max-w-md mx-auto mb-6">
                We couldn't find any businesses matching your criteria. Try adjusting your search terms or filters.
              </p>
              <button
                onClick={() => {
                  setFilterOptions({ businessType: '' });
                  setSearchQuery('');
                  setLocationQuery('');
                  fetchBusinesses(true);
                }}
                className="px-6 py-3 bg-[#FF8C00] text-white rounded-lg hover:bg-[#FF8C00]/90 transition-colors"
              >
                Reset Filters
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessListPage; 